import { isNonEmptyString, isNull, isNullableString, isRecord } from "@/lib/typeGuards";

/**
 * The contract between this web app and the native shell in
 * sgf-customer-mobile. Both sides must agree on this file — keep it copied
 * verbatim (or published as a shared package) rather than re-typed.
 *
 * Wire protocol
 * -------------
 * web -> native : window.ReactNativeWebView.postMessage(JSON.stringify(request))
 * native -> web : window.__SGC_RECEIVE__(JSON.stringify(response | event))
 *
 * The native side pushes into a global function instead of window.postMessage
 * because postMessage delivery differs between iOS and Android in
 * react-native-webview; an injected function call is deterministic on both.
 *
 * Versioning
 * ----------
 * Every message carries `v`. Old app versions stay installed on customer
 * devices for a long time, so this version may only ever be bumped alongside a
 * native release that understands the new shape — never as a silent change.
 */
export const BRIDGE_PROTOCOL_VERSION = 1;

/** Errors the native shell may return, plus the ones this module raises itself. */
export const BRIDGE_ERROR_CODES = {
  /** No native shell present (running in a plain browser tab). */
  unavailable: "bridge/unavailable",
  /** The shell did not answer within VITE_BRIDGE_TIMEOUT_MS. */
  timeout: "bridge/timeout",
  /** The shell answered with a payload that does not match this contract. */
  malformedResponse: "bridge/malformed-response",
  /** The installed app version does not implement the requested method. */
  unsupportedMethod: "bridge/unsupported-method",
  /** The shell attempted the call and it failed. */
  nativeFailure: "bridge/native-failure",
} as const;

export type BridgeErrorCode =
  (typeof BRIDGE_ERROR_CODES)[keyof typeof BRIDGE_ERROR_CODES];

export interface ExchangeCodeResult {
  /** null when the shell has no session to mint a code from. */
  code: string | null;
}

/**
 * Every callable method with its params and result. This interface is the
 * single declaration of what the bridge can do — call-site types come straight
 * from here.
 */
export interface BridgeMethods {
  /**
   * Ask the shell for a fresh one-time code to exchange for a web session.
   *
   * The shell never hands over the access token itself — it mints a code
   * through the API and passes only that, so a compromised page cannot walk
   * away with the customer's real credential.
   */
  "session.exchangeCode": { params: void; result: ExchangeCodeResult };
  /** Drop the session and return the user to the PIN screen. */
  "session.signOut": { params: void; result: null };

  /** Close this WebView and pop back to the previous native screen. */
  "nav.back": { params: void; result: null };
  /** Update the native header title (no-op when the web app draws its own). */
  "nav.setTitle": { params: { title: string }; result: null };
  "nav.setHeaderVisible": { params: { visible: boolean }; result: null };
  /** Navigate to a native screen by its SCREENS_NAME key. */
  "nav.push": {
    params: { screen: string; params?: Record<string, unknown> };
    result: null;
  };

  /** Save a remote or data: image URL to the device gallery. */
  "file.saveImage": { params: { url: string }; result: { saved: boolean } };
  /** Open a URL outside the WebView (system browser / another app). */
  "app.openExternal": { params: { url: string }; result: null };
  /** Show the native snackbar. */
  "ui.toast": {
    params: { kind: "success" | "error" | "info"; message: string };
    result: null;
  };
}

export type BridgeMethod = keyof BridgeMethods;
export type BridgeParams<M extends BridgeMethod> = BridgeMethods[M]["params"];
export type BridgeResult<M extends BridgeMethod> = BridgeMethods[M]["result"];

export type BridgeResponse =
  | { v: typeof BRIDGE_PROTOCOL_VERSION; id: string; ok: true; result: unknown }
  | {
      v: typeof BRIDGE_PROTOCOL_VERSION;
      id: string;
      ok: false;
      error: { code: string; message: string };
    };

/** Unsolicited messages pushed by the native shell. */
export type BridgeEvent =
  | {
      v: typeof BRIDGE_PROTOCOL_VERSION;
      type: "session.changed";
      accessToken: string | null;
    }
  | { v: typeof BRIDGE_PROTOCOL_VERSION; type: "app.foreground" }
  | { v: typeof BRIDGE_PROTOCOL_VERSION; type: "app.background" }
  /**
   * Android hardware back / iOS header back. The web app decides whether to go
   * back inside its own history or to call `nav.back` and close the WebView.
   */
  | { v: typeof BRIDGE_PROTOCOL_VERSION; type: "nav.backPressed" };

export type BridgeEventType = BridgeEvent["type"];

const BRIDGE_EVENT_TYPES: readonly BridgeEventType[] = [
  "session.changed",
  "app.foreground",
  "app.background",
  "nav.backPressed",
];

function isExchangeCodeResult(value: unknown): value is ExchangeCodeResult {
  return isRecord(value) && isNullableString(value["code"]);
}

function isSaveImageResult(value: unknown): value is { saved: boolean } {
  return isRecord(value) && typeof value["saved"] === "boolean";
}

/**
 * Runtime check for each method's result. Anything the shell sends back is
 * untrusted — an older app version may answer with a different shape.
 */
export const BRIDGE_RESULT_GUARDS = {
  "session.exchangeCode": isExchangeCodeResult,
  "session.signOut": isNull,
  "nav.back": isNull,
  "nav.setTitle": isNull,
  "nav.setHeaderVisible": isNull,
  "nav.push": isNull,
  "file.saveImage": isSaveImageResult,
  "app.openExternal": isNull,
  "ui.toast": isNull,
} satisfies {
  [M in BridgeMethod]: (value: unknown) => value is BridgeResult<M>;
};

function isBridgeResponse(value: unknown): value is BridgeResponse {
  if (!isRecord(value)) return false;
  if (value["v"] !== BRIDGE_PROTOCOL_VERSION) return false;
  if (!isNonEmptyString(value["id"])) return false;

  if (value["ok"] === true) return true;
  if (value["ok"] !== false) return false;

  const error = value["error"];
  return (
    isRecord(error) &&
    isNonEmptyString(error["code"]) &&
    typeof error["message"] === "string"
  );
}

function isBridgeEvent(value: unknown): value is BridgeEvent {
  if (!isRecord(value)) return false;
  if (value["v"] !== BRIDGE_PROTOCOL_VERSION) return false;

  const type = value["type"];
  if (typeof type !== "string") return false;
  if (!BRIDGE_EVENT_TYPES.includes(type as BridgeEventType)) return false;

  if (type === "session.changed") {
    return isNullableString(value["accessToken"]);
  }
  return true;
}

/**
 * Validates a message pushed in by the native shell. Returns null for anything
 * off-contract, which the caller drops rather than acts on.
 */
export function parseBridgeIncoming(
  value: unknown,
): BridgeResponse | BridgeEvent | null {
  if (isBridgeResponse(value)) return value;
  if (isBridgeEvent(value)) return value;
  return null;
}
